import {
  FloatingPosition,
  Popover,
  PopoverProps,
  ScrollArea,
} from '@mantine/core';
import { MouseEvent, ReactNode, useCallback, useRef, useState } from 'react';

import styles from './Tooltip.module.css';

// Mantine's own `size: true` also writes maxWidth, which would clobber `maw`.
const fitViewportMiddlewares: PopoverProps['middlewares'] = {
  flip: { fallbackStrategy: 'bestFit' },
  size: {
    padding: 8,
    apply: ({ availableHeight, elements }) => {
      elements.floating.style.maxHeight = `${availableHeight}px`;
    },
  },
};

export type InteractiveTooltipProps = {
  /** Content rendered inside the tooltip dropdown */
  content: ReactNode;
  /** Trigger element */
  children: ReactNode;
  /** Dropdown position relative to target */
  position?: FloatingPosition;
  /** Whether to show arrow */
  withArrow?: boolean;
  /** Arrow size in px */
  arrowSize?: number;
  /** Arrow offset in px */
  arrowOffset?: number;
  /** Delay in ms before opening on hover */
  openDelay?: number;
  /** Delay in ms before closing on mouse leave */
  closeDelay?: number;
  /** Controlled opened state */
  isOpened?: boolean;
  /** Called when opened state changes */
  onOpenChange?: (opened: boolean) => void;
  /** Whether the tooltip is disabled */
  isDisabled?: boolean;
  /** Stop click propagation from the trigger and the dropdown */
  shouldStopPropagation?: boolean;
  /** Max width of the dropdown */
  maw?: number | string;
  /** Additional props passed to the underlying Popover */
  popoverProps?: Omit<
    PopoverProps,
    | 'opened'
    | 'onChange'
    | 'position'
    | 'withArrow'
    | 'arrowSize'
    | 'arrowOffset'
    | 'disabled'
    | 'unstyled'
    | 'classNames'
    | 'children'
  >;
};

export const InteractiveTooltip = ({
  content,
  children,
  position = 'bottom',
  withArrow = true,
  arrowSize = 14,
  arrowOffset = 16,
  openDelay = 0,
  closeDelay = 150,
  isOpened: controlledOpened,
  onOpenChange,
  isDisabled = false,
  shouldStopPropagation = false,
  maw = 360,
  popoverProps,
}: InteractiveTooltipProps) => {
  const handleClick = (event: MouseEvent) => {
    if (shouldStopPropagation) event.stopPropagation();
  };
  const [uncontrolledOpened, setUncontrolledOpened] = useState(false);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const isControlled = controlledOpened !== undefined;
  const isOpen = isControlled ? controlledOpened : uncontrolledOpened;

  const clearTimeouts = useCallback(() => {
    clearTimeout(openTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);
  }, []);

  const open = useCallback(() => {
    clearTimeouts();
    if (openDelay === 0) {
      if (!isControlled) setUncontrolledOpened(true);
      onOpenChange?.(true);
    } else {
      openTimeoutRef.current = setTimeout(() => {
        if (!isControlled) setUncontrolledOpened(true);
        onOpenChange?.(true);
      }, openDelay);
    }
  }, [clearTimeouts, openDelay, isControlled, onOpenChange]);

  const close = useCallback(() => {
    clearTimeouts();
    if (closeDelay === 0) {
      if (!isControlled) setUncontrolledOpened(false);
      onOpenChange?.(false);
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        if (!isControlled) setUncontrolledOpened(false);
        onOpenChange?.(false);
      }, closeDelay);
    }
  }, [clearTimeouts, closeDelay, isControlled, onOpenChange]);

  const handleDropdownMouseEnter = useCallback(() => {
    clearTimeouts();
  }, [clearTimeouts]);

  const handleDropdownMouseLeave = useCallback(() => {
    close();
  }, [close]);

  return (
    <Popover
      opened={isOpen}
      position={position}
      withArrow={withArrow}
      arrowSize={arrowSize}
      arrowOffset={arrowOffset}
      arrowPosition="center"
      disabled={isDisabled}
      closeOnClickOutside
      onClose={() => {
        if (!isControlled) setUncontrolledOpened(false);
        onOpenChange?.(false);
      }}
      unstyled
      classNames={{
        dropdown: styles.interactiveDropdown,
        arrow: styles.arrow,
      }}
      {...popoverProps}
      middlewares={{ ...fitViewportMiddlewares, ...popoverProps?.middlewares }}
    >
      <Popover.Target>
        <span
          onMouseEnter={open}
          onMouseLeave={close}
          onClick={handleClick}
          style={{ display: 'inline-block' }}
        >
          {children}
        </span>
      </Popover.Target>

      <Popover.Dropdown
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
        onClick={handleClick}
        style={{ maxWidth: maw }}
      >
        {/* The scroller sits inside the dropdown: overflow on the dropdown itself would clip the arrow. */}
        <ScrollArea.Autosize type="always" offsetScrollbars="present">
          <div className={styles.interactiveContent}>{content}</div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
};
